import logger from "../../utils/logger";
import React, { Fragment, useMemo, useState, useCallback } from "react";
import {
  Alert,
  Button,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Table,
} from "reactstrap";

// react table
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  flexRender,
} from "@tanstack/react-table";

// redux toolkit
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { createOrder } from "../../slices/apps/orderSlice";
import {
  fetchCustomersBySearch,
  clearCustomersBySearch,
  fetchCustomerStatus,
} from "../../slices/apps/customerSlice";

// Formik validation
import * as Yup from "yup";
import { useFormik, FormikProvider } from "formik";

// react select
import Select from "react-select";

const getSelectStyles = (validation, fieldName) => ({
  control: (base, state) => ({
    ...base,
    borderColor:
      validation.touched[fieldName] && validation.errors[fieldName]
        ? "#e30a17"
        : state.isFocused
        ? "#80bdff"
        : "#e9ebec",
  }),
});

const columnHelper = createColumnHelper();

const couponAmountOptions = [
  { value: 0, label: "Yok" },
  { value: 250, label: "250 TL" },
  { value: 500, label: "500 TL" },
  { value: 1000, label: "1000 TL" },
];

const productOptions = [
  { value: "Yedek Parça", label: "Yedek Parça" },
  { value: "Madeni Yağ", label: "Madeni Yağ" },
  { value: "Lastik", label: "Lastik" },
  { value: "İşçilik", label: "İşçilik" },
];

const initialNewItemState = {
  name: "",
  quantity: 1,
  price: "",
  description: "",
};

const AddModal = () => {
  const dispatch = useDispatch();
  const [modal, setModal] = useState(false);

  const {
    customersBySearch,
    customersBySearchLoading,
    createOrderLoading,
    customerStatus,
  } = useSelector(
    (state) => ({
      customersBySearch: state.customer.customersBySearch,
      customersBySearchLoading: state.customer.customersBySearchLoading,
      createOrderLoading: state.order.createOrderLoading,
      customerStatus: state.customer.customerStatus,
    }),
    shallowEqual
  );

  const [newItem, setNewItem] = useState(initialNewItemState);
  const [newItemErrors, setNewItemErrors] = useState({});

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      customerId: "",
      workOrderCode: "",
      couponAmount: 0,
      items: [],
    },
    validationSchema: Yup.object({
      customerId: Yup.string().required("Lütfen bir müşteri seçiniz."),
      workOrderCode: Yup.string().required("Lütfen iş emri numarası giriniz."),
      couponAmount: Yup.number().required("Lütfen hediye çeki tutarı seçiniz."),
      items: Yup.array().min(
        1,
        "Servis oluşturmak için en az bir ürün eklemelisiniz."
      ),
    }),
    onSubmit: async (values) => {
      const orderDetails = values.items.map((item) => ({
        productName: item.name,
        productDescription: item.description,
        quantity: Number(item.quantity),
        price: Number(item.price),
      }));
      const finalPayload = {
        customerId: values.customerId,
        workOrderCode: values.workOrderCode,
        couponAmount: values.couponAmount,
        orderDetails,
      };
      try {
        await dispatch(createOrder(finalPayload)).unwrap();
        resetAllForms();
        setModal(false);
      } catch (error) {
        logger.error("Servis oluşturulurken hata oluştu:", error);
      }
    },
  });

  const resetAllForms = () => {
    setNewItem(initialNewItemState);
    setNewItemErrors({});
    validation.resetForm();
    dispatch(clearCustomersBySearch());
  };

  const toggle_modal = (isOpen) => {
    resetAllForms();
    setModal(isOpen);
    if (!isOpen) {
      resetAllForms();
    }
  };

  const handleCustomerInputChange = (inputValue, actionMeta) => {
    if (actionMeta.action !== "input-change") return;
    if (inputValue && inputValue.length >= 3) {
      dispatch(fetchCustomersBySearch(inputValue));
    } else {
      dispatch(clearCustomersBySearch());
    }
  };

  const customerOptions = useMemo(
    () =>
      customersBySearch?.map((customer) => ({
        value: customer.id,
        label: customer.fullName,
      })) || [],
    [customersBySearch]
  );

  const handleAddItem = () => {
    const errors = {};
    if (!newItem.name.trim()) errors.name = "Ürün adı zorunludur";
    if (!newItem.price) errors.price = "Fiyat zorunludur";
    else if (isNaN(newItem.price) || Number(newItem.price) <= 0)
      errors.price = "Geçerli bir fiyat girin";
    if (!newItem.quantity) errors.quantity = "Adet zorunludur";
    else if (
      !Number.isInteger(Number(newItem.quantity)) ||
      Number(newItem.quantity) < 1
    )
      errors.quantity = "Adet en az 1 olmalıdır";

    setNewItemErrors(errors);

    if (Object.keys(errors).length === 0) {
      validation.setFieldValue("items", [...validation.values.items, newItem]);
      setNewItem(initialNewItemState);
    }
  };

  const handleRemoveItem = useCallback(
    (indexToRemove) => {
      const newItems = validation.values.items.filter(
        (_, index) => index !== indexToRemove
      );
      validation.setFieldValue("items", newItems);
    },
    [validation]
  );

  const calculateTotal = useMemo(() => {
    return validation.values.items.reduce((total, item) => {
      const price = parseFloat(item.price) || 0;
      const quantity = parseInt(item.quantity, 10) || 0;
      return total + price * quantity;
    }, 0);
  }, [validation.values.items]);

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "actions",
        size: 46,
        minSize: 46,
        maxSize: 46,
        cell: ({ row }) => (
          <Button
            color="link"
            className="text-primary btn-icon p-0"
            onClick={() => handleRemoveItem(row.index)}
          >
            <i className="ri-delete-bin-line"></i>
          </Button>
        ),
      }),
      columnHelper.accessor("name", {
        header: "Ürün Adı",
        size: "auto",
        minSize: 200,
        maxSize: 400,
        cell: ({ row }) => (
          <div className="text-truncate">
            {row.original.name}
            {row.original.description && (
              <p className="text-muted mb-0 small fst-italic">
                {row.original.description}
              </p>
            )}
          </div>
        ),
      }),
      columnHelper.accessor("quantity", {
        header: "Adet",
        size: 60,
        minSize: 60,
        maxSize: 60,
      }),
      columnHelper.accessor("price", {
        header: "Birim Fiyat",
        size: 120,
        minSize: 120,
        maxSize: 120,
        cell: (info) =>
          parseFloat(info.getValue()).toLocaleString("tr-TR", {
            style: "currency",
            currency: "TRY",
          }),
      }),
      columnHelper.accessor((row) => row.quantity * row.price, {
        id: "total",
        header: "Toplam",
        size: 120,
        minSize: 120,
        maxSize: 120,
        cell: (info) =>
          info
            .getValue()
            .toLocaleString("tr-TR", { style: "currency", currency: "TRY" }),
      }),
    ],
    [handleRemoveItem]
  );

  const table = useReactTable({
    data: validation.values.items,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const blockInvalidChars = (e) => {
    if (["-", "e", "+", "E"].includes(e.key)) {
      e.preventDefault();
    }
  };

  const isDiscountApplied =
    validation.values?.customerId &&
    customerStatus?.statusCode == 0 &&
    calculateTotal > 5000;

  const finalTotal = isDiscountApplied ? calculateTotal - 1000 : calculateTotal;

  return (
    <Fragment>
      <Button
        className="btn btn-label btn"
        color="primary"
        type="button"
        onClick={() => toggle_modal(true)}
      >
        <i className="ri-add-line label-icon align-middle me-2"></i> Servis
        Oluştur
      </Button>

      <Modal
        size="lg"
        isOpen={modal}
        toggle={() => toggle_modal(false)}
        centered
      >
        <ModalHeader
          toggle={() => toggle_modal(false)}
          className="border-0 pb-0"
        >
          Servis Oluştur
        </ModalHeader>
        <FormikProvider value={validation}>
          <Form onSubmit={validation.handleSubmit} autoComplete="off">
            <ModalBody className="p-4">
              <Row className="gy-3">
                <Col lg={12}>
                  <Label htmlFor="customerId-select">Müşteri</Label>
                  <Select
                    id="customerId-select"
                    name="customerId"
                    placeholder="Müşteri aramak için en az 3 harf giriniz..."
                    isClearable
                    options={customerOptions}
                    menuPlacement="auto"
                    value={
                      customerOptions.find(
                        (opt) => opt.value === validation.values.customerId
                      ) || null
                    }
                    onChange={(selectedOption) => {
                      const customerId = selectedOption
                        ? selectedOption.value
                        : "";
                      validation.setFieldValue("customerId", customerId);

                      if (selectedOption) {
                        dispatch(fetchCustomerStatus(customerId));
                      } else {
                        dispatch(clearCustomersBySearch());
                      }
                    }}
                    onInputChange={handleCustomerInputChange}
                    isLoading={customersBySearchLoading}
                    filterOption={null}
                    noOptionsMessage={({ inputValue }) => {
                      if (customersBySearchLoading)
                        return "Müşteriler aranıyor...";
                      if (inputValue && inputValue.length < 3)
                        return "Aramak için en az 3 harf giriniz.";
                      return "Müşteri bulunamadı.";
                    }}
                    styles={getSelectStyles(validation, "customerId")}
                  />
                  {validation.touched.customerId &&
                  validation.errors.customerId ? (
                    <div className="invalid-feedback d-block">
                      {validation.errors.customerId}
                    </div>
                  ) : null}

                  {validation.values.customerId && customerStatus?.status && (
                    <Alert
                      color="primary"
                      className="mt-3 mb-0 d-flex align-items-center"
                    >
                      <i className="ri-information-line me-2 fs-5"></i>
                      <div>{customerStatus.status}</div>
                    </Alert>
                  )}
                </Col>

                <Col md={6}>
                  <Label>İş Emri Numarası</Label>
                  <Input
                    type="text"
                    name="workOrderCode"
                    placeholder="ABY000"
                    value={validation.values.workOrderCode}
                    maxLength={50}
                    onChange={validation.handleChange}
                    invalid={
                      validation.touched.workOrderCode &&
                      !!validation.errors.workOrderCode
                    }
                  />
                  {validation.touched.workOrderCode &&
                    validation.errors.workOrderCode && (
                      <FormFeedback>
                        {validation.errors.workOrderCode}
                      </FormFeedback>
                    )}
                </Col>

                {validation.values.customerId &&
                  customerStatus.statusCode == 1 && (
                    <Col lg={6}>
                      <Label>Hediye Çeki</Label>
                      <Select
                        name="couponAmount"
                        placeholder="Seçiniz..."
                        options={couponAmountOptions}
                        menuPlacement="auto"
                        value={
                          couponAmountOptions.find(
                            (opt) =>
                              opt.value === validation.values.couponAmount
                          ) || null
                        }
                        onChange={(option) =>
                          validation.setFieldValue(
                            "couponAmount",
                            option ? option.value : 0
                          )
                        }
                        styles={getSelectStyles(validation, "couponAmount")}
                      />
                      {validation.touched.couponAmount &&
                        validation.errors.couponAmount && (
                          <div className="invalid-feedback d-block">
                            {validation.errors.couponAmount}
                          </div>
                        )}
                    </Col>
                  )}

                <Col lg={12}>
                  <hr className="m-0 p-0 mt-1 mb-1" />
                </Col>

                <Col md={6}>
                  <Label>Ürün Adı</Label>
                  <Select
                    name="name"
                    placeholder="Ürün seçiniz..."
                    options={productOptions}
                    menuPlacement="auto"
                    value={
                      productOptions.find(
                        (opt) => opt.value === newItem.name
                      ) || null
                    }
                    onChange={(selectedOption) => {
                      setNewItem({
                        ...newItem,
                        name: selectedOption ? selectedOption.value : "",
                      });
                    }}
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        borderColor: !!newItemErrors.name
                          ? "#e30a17"
                          : state.isFocused
                          ? "#80bdff"
                          : "#e9ebec",
                      }),
                    }}
                  />
                  {newItemErrors.name && (
                    <div className="invalid-feedback d-block">
                      {newItemErrors.name}
                    </div>
                  )}
                </Col>
                <Col md={3}>
                  <Label>Adet</Label>
                  <Input
                    type="number"
                    name="quantity"
                    value={newItem.quantity}
                    onChange={(e) =>
                      setNewItem({
                        ...newItem,
                        quantity: e.target.value.replace(/[^0-9]/g, ""),
                      })
                    }
                    onKeyDown={blockInvalidChars}
                    invalid={!!newItemErrors.quantity}
                    placeholder="1"
                  />
                  {newItemErrors.quantity && (
                    <FormFeedback>{newItemErrors.quantity}</FormFeedback>
                  )}
                </Col>
                <Col md={3}>
                  <Label>B. Fiyat (₺)</Label>
                  <Input
                    type="number"
                    name="price"
                    value={newItem.price}
                    onChange={(e) =>
                      setNewItem({
                        ...newItem,
                        price: e.target.value
                          .replace(/[^0-9.]/g, "")
                          .replace(/(\..*)\./g, "$1"),
                      })
                    }
                    onKeyDown={blockInvalidChars}
                    invalid={!!newItemErrors.price}
                    placeholder="1500"
                  />
                  {newItemErrors.price && (
                    <FormFeedback>{newItemErrors.price}</FormFeedback>
                  )}
                </Col>
                <Col md={12}>
                  <Label>Açıklama</Label>
                  <Input
                    type="textarea"
                    rows="2"
                    value={newItem.description}
                    onChange={(e) =>
                      setNewItem({
                        ...newItem,
                        description: e.target.value,
                      })
                    }
                    maxLength={250}
                    style={{ resize: "none" }}
                    placeholder="Cihazın seri numarası, ek notlar vb."
                  />
                </Col>

                <Col md={12}>
                  <p className="fs-10 text-muted">
                    *Birim Fiyatı KDV'siz Girin
                  </p>
                </Col>
              </Row>

              <Button
                className="btn btn-label btn"
                color="primary"
                type="button"
                onClick={handleAddItem}
              >
                <i className="ri-add-line label-icon align-middle me-2"></i>{" "}
                Kalemi Ekle
              </Button>

              <div className="mt-4">
                {table.getRowModel().rows.length > 0 ? (
                  <Table borderless className="align-middle">
                    <thead className="table-light text-muted">
                      {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                          {headerGroup.headers.map((header) => (
                            <th
                              key={header.id}
                              style={{
                                width: header.column.columnDef.size,
                                minWidth: header.column.columnDef.minSize,
                                maxWidth: header.column.columnDef.maxSize,
                              }}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody>
                      {table.getRowModel().rows.map((row) => (
                        <tr key={row.id} className="border-bottom">
                          {row.getVisibleCells().map((cell) => (
                            <td key={cell.id}>
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <div className="text-center text-muted py-5">
                    <p className="mb-0">Henüz ürün eklenmedi.</p>
                  </div>
                )}
              </div>

              {validation.values.items.length > 0 && (
                <div className="d-flex justify-content-end mt-3">
                  <div style={{ width: "280px" }}>
                    {isDiscountApplied && (
                      <Fragment>
                        <div className="d-flex justify-content-between mb-1">
                          <small>Ara Toplam:</small>
                          <small>
                            {calculateTotal.toLocaleString("tr-TR", {
                              style: "currency",
                              currency: "TRY",
                            })}
                          </small>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <small>İndirim:</small>
                          <small className="fw-medium">
                            {(-1000).toLocaleString("tr-TR", {
                              style: "currency",
                              currency: "TRY",
                            })}
                          </small>
                        </div>
                      </Fragment>
                    )}
                    <div className="d-flex justify-content-between fw-bold">
                      <span>Toplam Tutar</span>
                      <span>
                        {finalTotal.toLocaleString("tr-TR", {
                          style: "currency",
                          currency: "TRY",
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div className="d-flex justify-content-between align-items-center mt-4">
                <div>
                  {validation.touched.items &&
                    typeof validation.errors.items === "string" && (
                      <div className="text-danger fs-11">
                        {validation.errors.items}
                      </div>
                    )}
                </div>
                <Button
                  type="submit"
                  color="primary"
                  className="btn btn-label"
                  disabled={createOrderLoading}
                >
                  <i className="ri-check-line label-icon align-middle me-2"></i>{" "}
                  Oluştur
                </Button>
              </div>
            </ModalBody>
          </Form>
        </FormikProvider>
      </Modal>
    </Fragment>
  );
};

export default AddModal;
